import * as React from 'react';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface ConfirmDialogProps {
	isOpen: boolean;
	onClose?: () => void;
	title: React.ReactNode;
	description: React.ReactNode;
	onConfirm: () => void;
	confirmText?: string;
	cancelText?: string;
	confirmButtonClassName?: string;
	cancelButtonClassName?: string;
	className?: string;
	isWarningDialog?: boolean;
	preventClose?: boolean;
	isDeleteDialog?: boolean;
	footer?: React.ReactNode;
}

export function ConfirmDialog({
	isOpen,
	onClose,
	title,
	description,
	onConfirm,
	confirmText = 'Confirm',
	cancelText = 'Cancel',
	confirmButtonClassName,
	cancelButtonClassName,
	className = '',
	isWarningDialog = false,
	preventClose = false,
	isDeleteDialog = false,
	footer,
}: ConfirmDialogProps) {
	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!preventClose && !open && onClose) {
					onClose();
				}
			}}
		>
			<DialogContent
				className={cn(
					className,
					isDeleteDialog &&
						'border-2 border-red-500 dark:border-red-700'
				)}
			>
				<DialogHeader>
					{isDeleteDialog && (
						<div className='flex flex-col items-center mb-4'>
							<img
								src='/logo.png'
								alt='Prometheus'
								className='h-12 mb-4'
							/>
							<motion.div
								animate={{
									scale: [1, 1.1, 1],
								}}
								transition={{
									duration: 2,
									repeat: Infinity,
									ease: 'easeInOut',
								}}
								className='text-5xl mb-2'
							>
								🗑️
							</motion.div>
							<motion.div
								animate={{
									opacity: [0.5, 1, 0.5],
								}}
								transition={{
									duration: 1.5,
									repeat: Infinity,
									ease: 'easeInOut',
								}}
								className='text-red-500 dark:text-red-400 text-sm font-semibold uppercase tracking-wider'
							>
								Permanent Delete Operation
							</motion.div>
						</div>
					)}
					<DialogTitle
						className={cn(
							'text-lg font-semibold leading-none tracking-tight',
							isDeleteDialog &&
								'text-center text-xl text-red-600 dark:text-red-400'
						)}
					>
						{title}
					</DialogTitle>
					<DialogDescription
						className={cn(
							'text-sm text-muted-foreground',
							isDeleteDialog && 'text-center mt-4 text-base'
						)}
					>
						{description}
						{isDeleteDialog && (
							<div className='mt-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-6'>
								<div className='text-red-600 dark:text-red-400 font-medium'>
									⚠️ Warning:
								</div>
								<ul className='list-disc list-inside text-sm text-red-600/90 dark:text-red-400/90 space-y-1 mt-2'>
									<li>This action cannot be undone.</li>
									<li>
										All associated data will be permanently
										deleted.
									</li>
									<li>
										This includes any analysis or related
										content.
									</li>
								</ul>
							</div>
						)}
					</DialogDescription>
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<DialogFooter>
						<Button
							variant='outline'
							className={cn(
								'text-lg p-8 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-gray-50 dark:text-white border-0 font-semibold',
								isDeleteDialog &&
									'from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800',
								cancelButtonClassName
							)}
							onClick={preventClose ? undefined : onClose}
						>
							{isDeleteDialog ? '🚫 Cancel' : cancelText}
						</Button>
						<Button
							onClick={onConfirm}
							className={cn(
								'text-lg p-8 bg-gradient-to-r from-pink-500 via-purple-500 to-purple-900 hover:from-pink-600 hover:via-purple-600 hover:to-purple-950 text-gray-50 dark:text-white border-0 font-bold',
								isDeleteDialog &&
									'from-red-500 to-red-700 hover:from-red-600 hover:to-red-800',
								isWarningDialog &&
									'bg-destructive hover:bg-destructive/90',
								confirmButtonClassName
							)}
						>
							{isDeleteDialog ? '🗑️ Delete Forever' : confirmText}
						</Button>
					</DialogFooter>
					{footer}
				</div>
			</DialogContent>
		</Dialog>
	);
}
